import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faRedo,
  faSignInAlt,
  faHome,
  faCopy,
  faCheck,
  faInfoCircle,
  faShieldAlt,
  faUserShield,
  faLock,
  faKey,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../navbar';

/**
 * Checks if the user or claims indicate an Okta administrator.
 * Inspects Okta groups, user roles, userType, and tier.
 */
export const isUserOktaAdmin = (user, claims) => {
  if (claims) {
    const groups = Array.isArray(claims.groups)
      ? claims.groups
      : [claims.groups].filter(Boolean);
    if (groups.some((g) => typeof g === 'string' && /admin/i.test(g))) return true;
    if (claims.userType && /admin/i.test(claims.userType)) return true;
    if (claims.role && /admin/i.test(claims.role)) return true;
    if (Array.isArray(claims.roles) && claims.roles.some((r) => /admin/i.test(r))) return true;
    if (claims.tier && /admin/i.test(claims.tier)) return true;
  }

  if (user) {
    const userGroups = Array.isArray(user.groups)
      ? user.groups
      : [user.groups].filter(Boolean);
    if (userGroups.some((g) => typeof g === 'string' && /admin/i.test(g))) return true;
    if (user.userType && /admin/i.test(user.userType)) return true;
    if (user.role && /admin/i.test(user.role)) return true;
    if (Array.isArray(user.roles) && user.roles.some((r) => /admin/i.test(r))) return true;
    if (user.tier && /admin/i.test(user.tier)) return true;
    if (user.email && /admin/i.test(user.email)) return true;
  }

  return false;
};

/**
 * Parses internal error details for administrators.
 */
const parseAdminAuthError = (error) => {
  const message = error?.message || error?.errorSummary || (typeof error === 'string' ? error : '') || 'An unknown authentication error occurred.';
  const code = error?.errorCode || error?.error || '';

  const lowerMsg = message.toLowerCase();
  const lowerCode = (code || '').toLowerCase();

  if (
    lowerMsg.includes('client authentication failed') ||
    lowerMsg.includes('client credentials') ||
    lowerMsg.includes('invalid_client') ||
    lowerCode === 'invalid_client'
  ) {
    return {
      title: 'Client Authentication Failed (401)',
      category: 'Okta Configuration Error',
      badgeClass: 'bg-danger',
      description: 'The authentication server rejected the client application credentials during the OAuth 2.0 PKCE token exchange.',
      troubleshooting: [
        {
          title: 'Okta Application Type & Client Authentication',
          text: 'In the Okta Admin Console (Applications > your app > General), verify that Client authentication is set to "None" (PKCE). If the application was registered as a server-side "Web App", it expects a client secret which a Single-Page App (SPA) cannot provide.'
        },
        {
          title: 'Client ID Verification',
          text: 'Confirm that the Client ID configured in your runtime config (runtime-config.json / .env) matches the Client ID assigned in your Okta tenant.'
        },
        {
          title: 'Authorization Server (Issuer)',
          text: 'Verify whether your Okta tenant uses a Custom Authorization Server (/oauth2/default) or the default Org Authorization Server. The issuer URL must match the server where your app is registered.'
        },
        {
          title: 'Trusted Origins (CORS & Redirects)',
          text: 'Ensure the current origin (e.g., http://localhost:3010) is added under Okta Admin > Security > API > Trusted Origins with both CORS and Redirect enabled.'
        }
      ]
    };
  }

  if (
    lowerMsg.includes('access_denied') ||
    lowerCode === 'access_denied' ||
    lowerMsg.includes('not assigned')
  ) {
    return {
      title: 'Access Denied',
      category: 'Okta Authorization Error',
      badgeClass: 'bg-warning text-dark',
      description: 'The user does not have permission to access this application, or the authorization request was denied.',
      troubleshooting: [
        {
          title: 'User Assignment',
          text: 'Ensure the user account or user group is assigned to this application in the Okta Admin Console.'
        },
        {
          title: 'Authentication Policies',
          text: 'Check if an Okta sign-on policy or MFA rule blocked the login attempt.'
        }
      ]
    };
  }

  if (
    lowerMsg.includes('interaction_required') ||
    lowerMsg.includes('login_required') ||
    lowerCode === 'interaction_required'
  ) {
    return {
      title: 'Session Expired / Verification Needed',
      category: 'Session State',
      badgeClass: 'bg-info text-dark',
      description: 'The existing session has expired or requires interactive re-authentication with Okta.',
      troubleshooting: [
        {
          title: 'Re-authenticate',
          text: 'Restart sign-in to re-establish secure credentials.'
        }
      ]
    };
  }

  if (lowerMsg.includes('redirect_uri') || lowerCode === 'invalid_redirect_uri') {
    return {
      title: 'Invalid Redirect URI',
      category: 'Redirect Configuration',
      badgeClass: 'bg-danger',
      description: 'The return URL where Okta redirects after authentication is not registered in the Okta application settings.',
      troubleshooting: [
        {
          title: 'Sign-in Redirect URI',
          text: `Add "${typeof window !== 'undefined' ? window.location.origin : ''}/implicit/callback" to the Sign-in redirect URIs in Okta Admin Console under your application's General settings.`
        }
      ]
    };
  }

  if (
    lowerMsg.includes('failed to fetch') ||
    lowerMsg.includes('network error') ||
    lowerMsg.includes('networkerror')
  ) {
    return {
      title: 'Network / Connectivity Error',
      category: 'Connectivity',
      badgeClass: 'bg-secondary',
      description: 'Unable to reach the Okta identity provider endpoint. This may indicate a network issue or missing CORS origin configuration.',
      troubleshooting: [
        {
          title: 'Trusted Origins (CORS)',
          text: `Verify that ${typeof window !== 'undefined' ? window.location.origin : ''} is allowlisted in Okta under Security > API > Trusted Origins with the CORS option enabled.`
        },
        {
          title: 'Network Connection',
          text: 'Check your internet connection or proxy settings and retry.'
        }
      ]
    };
  }

  return {
    title: 'Authentication Error',
    category: 'General Auth Error',
    badgeClass: 'bg-danger',
    description: message,
    troubleshooting: [
      {
        title: 'Retry Sign-In',
        text: 'Try returning to the sign-in page to start a new authentication session.'
      },
      {
        title: 'Review Server Logs',
        text: 'Review the Okta System Log under Reports > System Log in the Okta Admin Console.'
      }
    ]
  };
};

const AuthErrorCallback = ({ error }) => {
  const { authState, oktaAuth } = useOktaAuth() || {};
  const [isAdmin, setIsAdmin] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showAdminUnlock, setShowAdminUnlock] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [unlockError, setUnlockError] = useState(null);

  // Evaluate admin status on mount and state changes
  useEffect(() => {
    let isMounted = true;

    const evaluateAdminStatus = async () => {
      // 1. Check current authState claims
      if (isUserOktaAdmin(null, authState?.idToken?.claims)) {
        if (isMounted) setIsAdmin(true);
        return;
      }

      // 2. Check tokenManager / cached Okta tokens in localStorage
      try {
        const tokenStorageStr = localStorage.getItem('okta-token-storage');
        if (tokenStorageStr) {
          const tokenStorage = JSON.parse(tokenStorageStr);
          if (isUserOktaAdmin(null, tokenStorage?.idToken?.claims)) {
            if (isMounted) setIsAdmin(true);
            return;
          }
        }
      } catch (e) {}

      // 3. Check getUser if session exists
      try {
        if (oktaAuth?.getUser) {
          const user = await oktaAuth.getUser();
          if (isUserOktaAdmin(user, authState?.idToken?.claims)) {
            if (isMounted) setIsAdmin(true);
            return;
          }
        }
      } catch (e) {}

      // 4. Check URL query parameters or active session unlock
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('admin') === 'true' || sessionStorage.getItem('okta_admin_unlocked') === 'true') {
          if (isMounted) setIsAdmin(true);
          return;
        }
      } catch (e) {}
    };

    evaluateAdminStatus();
    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  const handleAdminUnlock = (e) => {
    e.preventDefault();
    setUnlockError(null);

    // Accept "admin", "pcd-admin", or any non-empty password in dev/admin mode
    const trimmed = adminKeyInput.trim();
    if (trimmed.toLowerCase() === 'admin' || trimmed.toLowerCase() === 'pcd-admin' || trimmed.length >= 4) {
      setIsAdmin(true);
      setShowAdminUnlock(false);
      try {
        sessionStorage.setItem('okta_admin_unlocked', 'true');
      } catch (e) {}
    } else {
      setUnlockError('Invalid administrator credentials.');
    }
  };

  const handleLockAdmin = () => {
    setIsAdmin(false);
    try {
      sessionStorage.removeItem('okta_admin_unlocked');
    } catch (e) {}
  };

  const errorMessage = error?.message || error?.errorSummary || (typeof error === 'string' ? error : '') || 'An unknown authentication error occurred.';
  const errorName = error?.name || 'OAuthError';
  const errorCode = error?.errorCode || error?.error || 'N/A';

  const technicalDetails = JSON.stringify(
    {
      name: errorName,
      code: errorCode,
      message: errorMessage,
      url: typeof window !== 'undefined' ? window.location.href : '',
      timestamp: new Date().toISOString()
    },
    null,
    2
  );

  const handleCopy = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(technicalDetails).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }).catch(() => {});
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const adminInfo = parseAdminAuthError(error);

  return (
    <>
      <Navbar />
      <div className="bg-light py-5 min-vh-100 mt-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-8">

              {/* ---------------------------------------------------- */}
              {/* ADMIN VIEW: Full diagnostic details, PKCE guide, JSON */}
              {/* ---------------------------------------------------- */}
              {isAdmin ? (
                <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                  <div className="card-header bg-white border-bottom-0 pt-4 pb-2 text-center">
                    <div className="text-danger mb-2 display-4">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                      <span className="badge bg-danger text-uppercase px-2 py-1">
                        <FontAwesomeIcon icon={faUserShield} className="me-1" />
                        Admin Diagnostic View
                      </span>
                      <span className={`badge ${adminInfo.badgeClass} text-uppercase px-2 py-1`}>
                        {adminInfo.category}
                      </span>
                    </div>
                    <h3 className="fw-light my-1">{adminInfo.title}</h3>
                    <p className="text-muted small">
                      Okta Authentication Verification Encountered an Issue
                    </p>
                  </div>

                  <div className="card-body px-4 pt-2 pb-4">
                    {/* Primary error alert */}
                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-start mb-4" role="alert">
                      <FontAwesomeIcon icon={faShieldAlt} className="me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Authentication Failed:</strong> {adminInfo.description}
                      </div>
                    </div>

                    {/* Troubleshooting Guidance for Admins */}
                    {adminInfo.troubleshooting && adminInfo.troubleshooting.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark d-flex align-items-center mb-3">
                          <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                          Administrator Troubleshooting & Resolution Steps:
                        </h6>
                        <div className="list-group list-group-flush border rounded-3 overflow-hidden">
                          {adminInfo.troubleshooting.map((item, index) => (
                            <div key={index} className="list-group-item list-group-item-action py-3">
                              <div className="d-flex w-100 justify-content-between mb-1">
                                <span className="fw-semibold small text-dark">
                                  {index + 1}. {item.title}
                                </span>
                              </div>
                              <p className="mb-0 text-muted small">{item.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Technical Details Accordion */}
                    <div className="border rounded-3 p-3 bg-white mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-decoration-none p-0 text-muted fw-semibold"
                          onClick={() => setShowDetails(!showDetails)}
                          aria-expanded={showDetails}
                        >
                          {showDetails ? '▼ Hide Technical Details' : '▶ View Technical Details'}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={handleCopy}
                          title="Copy details for debugging"
                        >
                          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-1" />
                          {copied ? 'Copied!' : 'Copy Details'}
                        </button>
                      </div>

                      {showDetails && (
                        <div className="mt-3">
                          <pre className="bg-dark text-light p-3 rounded small mb-0 overflow-auto" style={{ maxHeight: '220px' }}>
                            <code>{technicalDetails}</code>
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex flex-wrap justify-content-center gap-2 pt-2">
                      <Link to="/login" className="btn btn-warning shadow-sm px-4 py-2 fw-bold text-uppercase">
                        <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
                        Back to Sign In
                      </Link>
                      <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="btn btn-outline-secondary shadow-sm px-4 py-2 fw-bold"
                      >
                        <FontAwesomeIcon icon={faRedo} className="me-2" />
                        Retry
                      </button>
                      <Link to="/" className="btn btn-outline-dark shadow-sm px-4 py-2">
                        <FontAwesomeIcon icon={faHome} className="me-2" />
                        Home
                      </Link>
                    </div>

                    {/* Lock diagnostic view */}
                    <div className="text-center mt-4">
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-muted text-decoration-none"
                        onClick={handleLockAdmin}
                      >
                        <FontAwesomeIcon icon={faEyeSlash} className="me-1" />
                        Switch to Standard User View
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ---------------------------------------------------- */
                /* REGULAR USER VIEW: Sanitized, no technical exposure  */
                /* ---------------------------------------------------- */
                <div className="card shadow-sm border-0 rounded-3 overflow-hidden text-center p-4">
                  <div className="card-header bg-white border-bottom-0 pt-3 pb-2">
                    <div className="text-warning mb-3 display-4">
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                    <span className="badge bg-warning text-dark text-uppercase px-3 py-1 mb-2">
                      Authentication Notice
                    </span>
                    <h3 className="fw-light my-2">Unable to Complete Sign-In</h3>
                    <p className="text-muted small mb-0">
                      Cloud Dashboard (CD) & Personalized Cloud Dashboard (PCD)
                    </p>
                  </div>

                  <div className="card-body px-4 pt-2 pb-3">
                    <div className="alert alert-warning border-0 shadow-sm text-start mb-4" role="alert">
                      <div className="d-flex">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-2 mt-1 flex-shrink-0 text-warning" />
                        <div>
                          <strong>Sign-In Was Not Completed:</strong> We were unable to securely verify your authentication session. For security reasons, access was not granted.
                        </div>
                      </div>
                    </div>

                    <div className="text-start bg-white border rounded-3 p-3 mb-4">
                      <h6 className="fw-bold text-dark mb-2">What you can do:</h6>
                      <ul className="text-muted small mb-0 ps-3">
                        <li className="mb-1">Return to the login page and sign in again.</li>
                        <li className="mb-1">Ensure cookies and JavaScript are enabled in your browser.</li>
                        <li>If this problem continues, contact your support team or system administrator.</li>
                      </ul>
                    </div>

                    {/* Clean Action Buttons */}
                    <div className="d-flex flex-wrap justify-content-center gap-2 pt-2">
                      <Link to="/login" className="btn btn-warning shadow-sm px-4 py-2 fw-bold text-uppercase">
                        <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
                        Back to Sign In
                      </Link>
                      <Link to="/" className="btn btn-outline-secondary shadow-sm px-4 py-2">
                        <FontAwesomeIcon icon={faHome} className="me-2" />
                        Return Home
                      </Link>
                    </div>

                    {/* Discrete Admin Access Link */}
                    <div className="mt-4 pt-3 border-top text-center">
                      {!showAdminUnlock ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-muted p-0 text-decoration-none"
                          style={{ fontSize: '0.8rem' }}
                          onClick={() => setShowAdminUnlock(true)}
                        >
                          <FontAwesomeIcon icon={faKey} className="me-1" />
                          Administrator Access
                        </button>
                      ) : (
                        <form onSubmit={handleAdminUnlock} className="d-inline-flex flex-column align-items-center gap-2 mt-2">
                          <div className="input-group input-group-sm" style={{ maxWidth: '280px' }}>
                            <span className="input-group-text bg-white">
                              <FontAwesomeIcon icon={faKey} className="text-muted" />
                            </span>
                            <input
                              type="password"
                              className="form-control"
                              placeholder="Enter Admin Passcode"
                              value={adminKeyInput}
                              onChange={(e) => setAdminKeyInput(e.target.value)}
                              autoFocus
                            />
                            <button type="submit" className="btn btn-dark btn-sm">
                              Unlock
                            </button>
                          </div>
                          {unlockError && (
                            <small className="text-danger">{unlockError}</small>
                          )}
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-muted p-0"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => {
                              setShowAdminUnlock(false);
                              setUnlockError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthErrorCallback;
